import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import { apiRequest } from '../../utils/api';
import { getModulePermissions } from '../../utils/permissions';
import { getActiveCompanyId } from '../../utils/companyContext';

const DesignationSettings = () => {
  const navigate = useNavigate();
  const [activeCompanyId, setActiveCompanyId] = useState(getActiveCompanyId());
  const activeCompanyValue = activeCompanyId ? String(activeCompanyId) : '';
  const [formData, setFormData] = useState({ designation: '', slug: '', companyId: activeCompanyValue });
  const [designations, setDesignations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const activeCompany = companies.find((company) => String(company.id) === activeCompanyValue);

  const permissions = useMemo(() => getModulePermissions('settings-designation'), []);
  const canCreate = permissions.create;
  const canEdit = permissions.edit;
  const canDelete = permissions.delete;
  const canManage = canCreate || canEdit;
  const hasActions = canManage || canDelete;
  const isViewOnly = permissions.view && !hasActions;

  const resetForm = () => {
    setFormData({ designation: '', slug: '', companyId: activeCompanyValue });
    setEditingId(null);
    setFormErrors({});
  };

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await apiRequest('/api/admin/companies', {}, navigate);
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.message || 'Unable to load companies.');
    }
  }, [navigate]);

  const fetchDesignations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!activeCompanyValue) {
        setDesignations([]);
        setError('Please select a company from the top header.');
        return;
      }

      const query = new URLSearchParams({ companyId: activeCompanyValue });
      const endpoint = `/api/admin/settings/designations?${query.toString()}`;
      const response = await apiRequest(endpoint, {}, navigate);
      setDesignations(response.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load designations.');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyValue, navigate]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchDesignations();
  }, [fetchDesignations]);

  useEffect(() => {
    const syncActiveCompany = () => setActiveCompanyId(getActiveCompanyId());
    window.addEventListener('activeCompanyChanged', syncActiveCompany);
    window.addEventListener('storage', syncActiveCompany);
    return () => {
      window.removeEventListener('activeCompanyChanged', syncActiveCompany);
      window.removeEventListener('storage', syncActiveCompany);
    };
  }, []);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, companyId: activeCompanyValue }));
  }, [activeCompanyValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isEditing = Boolean(editingId);

    if (isEditing && !canEdit) {
      setError('You do not have permission to edit designations.');
      return;
    }

    if (!isEditing && !canCreate) {
      setError('You do not have permission to create designations.');
      return;
    }

    const trimmedDesignation = formData.designation.trim();
    const validationErrors = {};

    if (!trimmedDesignation) {
      validationErrors.designation = 'Designation is required.';
    }

    if (!formData.companyId) {
      validationErrors.companyId = 'Company is required.';
    }

    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      setError('');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMessage('');
    setFormErrors({});

    const payload = {
      designation: trimmedDesignation,
      companyId: Number(activeCompanyValue),
    };

    try {
      const isEditing = Boolean(editingId);
      const endpoint = isEditing
        ? `/api/admin/settings/designations/${editingId}`
        : '/api/admin/settings/designations';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await apiRequest(
        endpoint,
        { method, body: payload },
        navigate
      );

      setSuccessMessage(
        response.message || (isEditing ? 'Designation updated successfully.' : 'Designation added successfully.')
      );
      resetForm();
      fetchDesignations();
    } catch (err) {
      const message = err.message || 'Unable to save designation.';
      if (/company/i.test(message)) {
        setFormErrors({ companyId: message });
        setError('');
      } else if (/required|exists|already/i.test(message)) {
        setFormErrors({ designation: message });
        setError('');
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (designation) => {
    if (!canEdit) {
      navigate('/access-denied', { state: { from: '/settings/designation' } });
      return;
    }
    setEditingId(designation.id);
    setFormData({
      designation: designation.designation || '',
      slug: designation.slug || '',
      companyId: activeCompanyValue,
    });
    setSuccessMessage('');
    setError('');
    setFormErrors({});
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      navigate('/access-denied', { state: { from: '/settings/designation' } });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete designation?',
      text: 'Are you sure you want to delete this designation? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionId(id);
    setError('');
    setSuccessMessage('');
    try {
      const response = await apiRequest(
        `/api/admin/settings/designations/${id}`,
        { method: 'DELETE' },
        navigate
      );
      setSuccessMessage(response.message || 'Designation deleted successfully.');
      if (editingId === id) {
        resetForm();
      }
      fetchDesignations();
    } catch (err) {
      setError(err.message || 'Unable to delete designation.');
    } finally {
      setActionId(null);
    }
  };

  const isEditing = Boolean(editingId);

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Designation Settings</h4>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate('/settings')}
          >
            Back
          </button>
        </div>

        {isViewOnly && (
          <div className="alert alert-info" role="alert">
            You have view-only access. Create, edit, and delete actions are disabled.
          </div>
        )}

        {error && !canManage && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {canManage && (
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title mb-4">{isEditing ? 'Update Designation' : 'Add Designation'}</h5>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  {successMessage && (
                    <div className="alert alert-success" role="alert">
                      {successMessage}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <div className="form-floating">
                        <input
                          type="text"
                          className={`form-control ${formErrors.designation ? 'is-invalid' : ''}`}
                          id="designation"
                          placeholder="Enter Designation"
                          value={formData.designation}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, designation: value }));
                            if (formErrors.designation) {
                              setFormErrors((prev) => {
                                const { designation, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                        />
                        <label htmlFor="designation">Enter Designation *</label>
                        {formErrors.designation && (
                          <div className="invalid-feedback">{formErrors.designation}</div>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="form-floating">
                        <select
                          className={`form-select ${formErrors.companyId ? 'is-invalid' : ''}`}
                          id="designationCompanyId"
                          value={formData.companyId}
                          onChange={() => { }}
                          disabled
                        >
                          {activeCompanyValue && activeCompany ? (
                            <option value={activeCompanyValue}>{activeCompany.name}</option>
                          ) : (
                            <option value="">Select Company</option>
                          )}
                        </select>
                        <label htmlFor="designationCompanyId">Company *</label>

                        {formErrors.companyId && (
                          <div className="invalid-feedback d-block">
                            {formErrors.companyId}
                          </div>
                        )}
                      </div>
                    </div>

                    {isEditing && (
                      <div className="mb-3">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            id="designationSlug"
                            value={formData.slug}
                            placeholder="Slug"
                            disabled
                            readOnly
                          />
                          <label htmlFor="designationSlug">Slug</label>
                        </div>
                      </div>
                    )}
                    <button
                      type="submit"
                      className="btn btn-danger"
                      disabled={submitting || !activeCompanyValue}
                    >
                      {submitting
                        ? isEditing
                          ? 'Updating...'
                          : 'Saving...'
                        : isEditing
                          ? 'Update'
                          : 'Save'}
                    </button>
                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary ms-2"
                        onClick={resetForm}
                        disabled={submitting}
                      >
                        Cancel
                      </button>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">Existing Designations</h5>
                <div className="row g-3 align-items-end mb-3">
                  <div className="col-md-6 col-lg-4">
                    <div className="form-floating">
                      <select
                        className="form-select"
                        id="designation-company-filter"
                        value={activeCompanyValue}
                        onChange={() => { }}
                        disabled
                      >
                        {activeCompanyValue && activeCompany ? (
                          <option value={activeCompanyValue}>
                            {activeCompany.name}
                          </option>
                        ) : (
                          <option value="">Select Company</option>
                        )}
                      </select>
                      <label htmlFor="designation-company-filter">Company</label>
                    </div>
                  </div>
                </div>
                {loading ? (
                  <p className="text-muted mb-0">Loading designations...</p>
                ) : designations.length === 0 ? (
                  <p className="text-muted mb-0">No designations found.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>Designation</th>
                          <th>Company</th>
                          <th>Slug</th>
                          {hasActions && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {designations.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + 1}</td>
                            <td>{item.designation}</td>
                            <td>{item.companyName || '-'}</td>
                            <td>{item.slug}</td>
                            {hasActions && (
                              <td>
                                {canEdit && (
                                  <button
                                    className="btn btn-sm btn-warning me-2"
                                    onClick={() => handleEdit(item)}
                                    disabled={submitting || actionId !== null}
                                  >
                                    Edit
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(item.id)}
                                    disabled={actionId === item.id || submitting}
                                  >
                                    {actionId === item.id ? 'Deleting...' : 'Delete'}
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DesignationSettings;
